import { useState, useEffect } from 'react';
import { Layout } from '../layouts/Layout';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { systemParametersService } from '../services/systemParametersService';
import { useAuth } from '../hooks/useAuth';

export default function ParameterSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    min_sleep_duration: 180,
    sleep_data_interval: 60,
    heart_rate_interval: 5,
    blood_oxygen_interval: 10,
    pressure_interval: 10,
    location_interval: 15,
    deep_weight: 0.4,
    light_weight: 0.3,
    rem_weight: 0.3
  });

  useEffect(() => {
    if (user?.companyId) {
      loadParameters();
    }
  }, [user?.companyId]);

  const loadParameters = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await systemParametersService.getParameters(user?.companyId);
      
      if (data && data.sleep_quality_formula) {
        setFormData({
          min_sleep_duration: data.min_sleep_duration || 360,
          sleep_data_interval: data.sleep_data_interval || 60,
          heart_rate_interval: data.heart_rate_interval || 5,
          blood_oxygen_interval: data.blood_oxygen_interval || 10,
          pressure_interval: data.pressure_interval || 10,
          location_interval: data.location_interval || 15,
          deep_weight: data.sleep_quality_formula.deep_weight || 0.4,
          light_weight: data.sleep_quality_formula.light_weight || 0.3,
          rem_weight: data.sleep_quality_formula.rem_weight || 0.3
        });
      }
    } catch (err: any) {
      console.error('Error loading parameters:', err);
      setError(err?.message || 'Failed to load parameters');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const totalWeight = formData.deep_weight + formData.light_weight + formData.rem_weight;
    if (Math.abs(totalWeight - 1.0) > 0.01) {
      alert('Total weight must equal 1.0');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      await systemParametersService.updateParameters({
        company_id: user?.companyId,
        min_sleep_duration: formData.min_sleep_duration,
        sleep_data_interval: formData.sleep_data_interval,
        heart_rate_interval: formData.heart_rate_interval,
        blood_oxygen_interval: formData.blood_oxygen_interval,
        pressure_interval: formData.pressure_interval,
        location_interval: formData.location_interval,
        sleep_quality_formula: {
          deep_weight: formData.deep_weight,
          light_weight: formData.light_weight,
          rem_weight: formData.rem_weight
        }
      });
      
      alert('Parameter settings saved successfully');
      await loadParameters();
    } catch (err: any) {
      console.error('Error saving parameters:', err);
      setError(err?.message || 'Failed to save parameter settings');
      alert('Failed to save parameter settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: string, value: number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const totalWeight = formData.deep_weight + formData.light_weight + formData.rem_weight;
  const isWeightValid = Math.abs(totalWeight - 1.0) <= 0.01;

  return (
    <Layout>
      <Sidebar />
      <Header title="Parameter Settings" />

      <main className="ml-0 md:ml-64 pt-20 p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Parameter Settings</h1>
          <p className="text-gray-600">Kelola parameter sistem untuk monitoring kesehatan</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="inline-flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading...
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
            <button 
              onClick={loadParameters}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-4xl">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Sleep Parameters</h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Sleep Duration (minutes)
                </label>
                <input
                  type="number"
                  value={formData.min_sleep_duration}
                  onChange={(e) => handleChange('min_sleep_duration', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-green-200 focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
                <p className="text-sm text-gray-500 mt-1">Durasi tidur minimum yang dianggap valid</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sleep Data Sync Interval (minutes) <span className='rounded bg-yellow-100 border-yellow-400 p-1 text-xs'>Upcoming</span>
                </label>
                <input
                  type="number"
                  value={formData.sleep_data_interval}
                  onChange={(e) => handleChange('sleep_data_interval', parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-green-200 focus:ring-2 focus:ring-blue-500"
                  min="1" readOnly disabled
                />
                <p className="text-sm text-gray-500 mt-1">Interval pengiriman data tidur ke cloud</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Vital Signs Sync Intervals  <span className='rounded bg-yellow-100 border-yellow-400 p-1 text-xs'>Upcoming</span></h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Heart Rate (minutes)
                  </label>
                  <input
                    type="number"
                    value={formData.heart_rate_interval}
                    onChange={(e) => handleChange('heart_rate_interval', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-green-200 focus:ring-2 focus:ring-blue-500"
                    min="1" readOnly disabled
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Blood Oxygen (minutes)
                  </label>
                  <input
                    type="number"
                    value={formData.blood_oxygen_interval}
                    onChange={(e) => handleChange('blood_oxygen_interval', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-green-200 focus:ring-2 focus:ring-blue-500"
                    min="1" readOnly disabled
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Blood Pressure (minutes)
                  </label>
                  <input
                    type="number"
                    value={formData.pressure_interval}
                    onChange={(e) => handleChange('pressure_interval', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-green-200 focus:ring-2 focus:ring-blue-500"
                    min="1" readOnly disabled
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GPS/Location (minutes)
                  </label>
                  <input
                    type="number"
                    value={formData.location_interval}
                    onChange={(e) => handleChange('location_interval', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-green-200 focus:ring-2 focus:ring-blue-500"
                    min="1" readOnly disabled
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">Interval pengiriman data vital signs dan lokasi ke cloud</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Sleep Quality Formula  <span className='rounded bg-yellow-100 border-yellow-400 p-1 text-xs'>Upcoming</span></h2>
              <p className="text-sm text-gray-600 mb-4">
                Bobot untuk perhitungan kualitas tidur (total harus = 1.0)
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deep Sleep Weight
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.deep_weight}
                    onChange={(e) => handleChange('deep_weight', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-green-200 focus:ring-2 focus:ring-blue-500"
                    min="0"
                    max="1" readOnly disabled
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Light Sleep Weight
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.light_weight}
                    onChange={(e) => handleChange('light_weight', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-green-200 focus:ring-2 focus:ring-blue-500"
                    min="0"
                    max="1" readOnly disabled
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    REM Sleep Weight
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.rem_weight}
                    onChange={(e) => handleChange('rem_weight', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-green-200 focus:ring-2 focus:ring-blue-500"
                    min="0"
                    max="1" readOnly disabled
                  />
                </div>
              </div>
              
              <div className="mt-3 p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-800">
                  Total Weight: {totalWeight.toFixed(2)}
                  {!isWeightValid && (
                    <span className="text-red-600 ml-2">(Harus = 1.0)</span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving || !isWeightValid}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        )}
      </main>
    </Layout>
  );
}
